import type { QueryError } from 'src/queries/network.utils'

/**
 * A fresh Card Management idempotency key.
 *
 * The contract requires 16–255 visible ASCII characters; a UUID is 36 and
 * satisfies that in every branch here.
 *
 * Call this once per *distinct attempt*, never per network retry. Reusing a key
 * for a materially different request is `CARD-MGMT.6`, and rotating a key to
 * escape an in-progress or uncertain operation is explicitly forbidden — that
 * is how a caller double-applies a mutation whose first outcome was unknown.
 */
export const createCardManagementIdempotencyKey = (): string =>
  typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `cardmgmt-${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`

/**
 * The stable public error codes this app branches on. The full table lives in
 * the backend handoff; these are the ones with distinct *user-facing*
 * recoveries.
 */
export const CARD_MANAGEMENT_ERROR_CODES = {
  /** Billing contact changed; refresh and try again. */
  billingContactStaleVersion: 'CARD-MGMT.28',
  /** Card limits changed; refresh and try again. */
  limitsStaleVersion: 'CARD-MGMT.5',
  /** Card status changed; refresh and try again. */
  statusStaleVersion: 'CARD-MGMT.17',
  /** Card status does not allow this action. */
  statusConflict: 'CARD-MGMT.18',
  /** Geofence changed; refresh and try again. */
  geofenceStaleVersion: 'CARD-MGMT.22',
  /**
   * The country is not a valid selection for this card. Search results are a
   * hint, not a guarantee — card state or partner capability can change between
   * the search and the change.
   */
  geofenceInvalidCountry: 'CARD-MGMT.23',
  /** Server- or partner-owned maximum reached; no number is disclosed. */
  geofenceCountryLimit: 'CARD-MGMT.24',
  /** An operation is already active. */
  operationActive: 'CARD-MGMT.7',
  /** Outcome uncertain — never blindly reissue. */
  reconciliationRequired: 'CARD-MGMT.13',
} as const

/**
 * Read the `errorCode` off a normalized query error.
 *
 * The top-level field first: `normalizeQueryError` spreads the two-field
 * `ApiErrorWithCode` envelope straight onto the `QueryError`, so `errorCode`
 * is already there for the ordinary axios path.
 *
 * `originalError` is only the fallback, and deliberately second — it is
 * defined non-enumerable, so any spread or structured clone of the error drops
 * it, and the already-a-`QueryError` branch of `normalizeQueryError` sets it to
 * the QueryError itself, which has no `.response`. Reading it first meant both
 * of those cases silently returned undefined and every recovery in
 * `cardManagementErrorMessage` collapsed to the generic retry message.
 */
export const cardManagementErrorCode = (
  error: QueryError | null | undefined,
): string | undefined => {
  if (!error) return undefined

  if (typeof error.errorCode === 'string') {
    return error.errorCode
  }

  const original = (error as { originalError?: unknown }).originalError
  const body = (
    original as { response?: { data?: { errorCode?: unknown } } } | undefined
  )?.response?.data
  return typeof body?.errorCode === 'string' ? body.errorCode : undefined
}
